import { type ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Menu do celular: a mesma lateral num painel por cima da tela. A tela de baixo continua montada
 * (formulários e filtros não se perdem). Fecha com X, Esc, toque fora ou ao mudar de tela; o foco
 * fica no painel e volta ao botão do menu. `side` diz de onde o painel entra: da esquerda (a
 * lateral) ou de baixo (a grade de áreas, ADR 0128).
 */
export function MobileDrawer({
  onClose,
  children,
  side = 'left',
}: {
  onClose: () => void;
  children: ReactNode;
  side?: 'left' | 'bottom';
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>('button, a[href]')?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])',
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-ink/35"
        onMouseDown={() => closeRef.current()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.label')}
        className={`absolute flex flex-col shadow-[0_12px_32px_rgb(23_32_43/0.16)] ${
          side === 'bottom' ? 'inset-x-0 bottom-0' : 'inset-y-0 left-0 w-80 max-w-[85vw]'
        }`}
      >
        {children}
      </div>
    </div>
  );
}
